import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Notify } from '@nutui/nutui-react';
import {
    ConsumptionTaskID,
    getTaskConfigs,
    getAllTaskTypes,
    groupMissionsByType,
} from '../data/consumptionTaskData';
import { useConsumptionActivity } from '../data/consumptionActivity';

/**
 * 消耗任务展示组件 - 显示五种悬赏任务进度和预测。
 * 档位表、道具名称等均来自服务器配置接口，显隐由父级按 act.open 控制。
 */
const ConsumptionTaskView = ({ role, user, updateConfig }) => {
    const { act } = useConsumptionActivity();
    const [isRequesting, setIsRequesting] = useState(false);
    const targetLevel2 = act.requiredLv2 ?? 250; // 目标2级道具数量（服务器配置）
    const [selectedTasks, setSelectedTasks] = useState({
        [ConsumptionTaskID.招募]: 0,
        [ConsumptionTaskID.宝箱]: 0,
        [ConsumptionTaskID.捕获]: 0,
        [ConsumptionTaskID.盐罐]: 0,
        [ConsumptionTaskID.金砖]: 0,
    });
    const [isInitialized, setIsInitialized] = useState(false);

    // 获取玩家消耗进度
    const consumptionStatus = role?.actConsumptionStatus || {
        招募: 0,
        宝箱: 0,
        捕获: 0,
        盐罐: 0,
        金砖: 0,
    };

    // 当期服务器下发的档位表（按悬赏类型分组）
    const groupedMissions = useMemo(() => groupMissionsByType(act.missions), [act.missions]);

    // 活动期切换时重置初始化标记，按新档位重新初始化选中项
    useEffect(() => {
        setIsInitialized(false);
    }, [act.activityId]);

    // 获取每个任务可选的档位列表
    const getAvailableStages = (taskId, consumed) => {
        const taskConfigs = getTaskConfigs(groupedMissions, taskId);
        const availableStages = [];

        for (const config of taskConfigs) {
            if (config.num > consumed) {
                availableStages.push({
                    num: config.num,
                    rewardLv1: config.rewardLv1,
                    id: config.id,
                });
            }
        }

        return availableStages;
    };

    // 获取玩家道具数量（道具名来自服务器配置，与 role_info.items 键同源）
    const level1Items = role?.items?.[act.lv1ItemName] || 0;
    const level2Items = role?.items?.[act.lv2ItemName] || 0;
    const consumedLv2Items = role?.actConsumptionStatus?.consumedLv2Items || 0;

    // 计算每种任务已完成的档位和已领取的1级道具数量
    const taskData = useMemo(() => {
        const taskTypes = getAllTaskTypes(groupedMissions);
        return taskTypes.map(taskType => {
            const taskId = taskType.id;
            const taskConfigs = getTaskConfigs(groupedMissions, taskId);
            const consumed = consumptionStatus[taskType.name] || 0;

            // 计算已完成的档位和总奖励
            let completedStages = 0;
            let totalRewardLv1 = 0;
            let lastCompletedNum = 0;

            for (const config of taskConfigs) {
                if (consumed >= config.num) {
                    completedStages++;
                    totalRewardLv1 += config.rewardLv1;
                    lastCompletedNum = config.num;
                } else {
                    break;
                }
            }

            // 获取可选的档位
            const availableStages = getAvailableStages(taskId, consumed);

            // 计算预测：选择具体档位后能获得的1级道具数量
            const selectedTargetNum = selectedTasks[taskId] || 0;
            let predictedRewardLv1 = 0;

            if (selectedTargetNum > 0) {
                for (const config of taskConfigs) {
                    if (config.num > consumed && config.num <= selectedTargetNum) {
                        predictedRewardLv1 += config.rewardLv1;
                    }
                }
            }

            return {
                id: taskId,
                name: taskType.name,
                consumed,
                completedStages,
                totalRewardLv1,
                selectedTargetNum,
                predictedRewardLv1,
                availableStages,
                lastCompletedNum,
                maxStages: taskConfigs.length,
            };
        });
    }, [consumptionStatus, level1Items, level2Items, selectedTasks, groupedMissions]);

    // 计算总预测信息
    const predictionInfo = useMemo(() => {
        const totalPredictedLv1 = taskData.reduce((sum, task) => sum + task.predictedRewardLv1, 0);
        const totalRewardLv1 = taskData.reduce((sum, task) => sum + task.totalRewardLv1, 0);
        const totalUsedLv1 = totalRewardLv1 - level1Items;
        const totalLv1AfterPrediction = totalUsedLv1 + level1Items + totalPredictedLv1;
        const item2ActualProbability = totalUsedLv1 > 0 ? (level2Items + consumedLv2Items) / totalUsedLv1 : 0; // 首帧/未使用时 totalUsedLv1 可能为0，避免 0/0 渲染 NaN%

        // 计算预计能获得的2级道具数量
        const estimatedLv2 = Math.floor((totalPredictedLv1 + level1Items) * 0.25);
        const estimatedLv2AtLeast = Math.floor((totalPredictedLv1 + level1Items) * 0.2); // 保底获得的2级道具
        const remainingLv2Needed = Math.max(0, targetLevel2 - level2Items);
        const additionalLv1Needed = remainingLv2Needed * 4 - level1Items; // 考虑平均概率

        return {
            totalUsedLv1,
            item2ActualProbability,
            totalPredictedLv1,
            totalLv1AfterPrediction,
            estimatedLv2,
            estimatedLv2AtLeast,
            remainingLv2Needed,
            additionalLv1Needed,
            canReachTarget: estimatedLv2 >= remainingLv2Needed,
        };
    }, [taskData, level1Items, level2Items, consumedLv2Items, targetLevel2]);

    // 处理档位选择变化
    const handleStageChange = (taskId, targetNum) => {
        setSelectedTasks(prev => ({
            ...prev,
            [taskId]: prev[taskId] === targetNum ? 0 : targetNum,
        }));
    };

    // 重置所有选择
    const resetSelections = () => {
        setSelectedTasks({
            [ConsumptionTaskID.招募]: 0,
            [ConsumptionTaskID.宝箱]: 0,
            [ConsumptionTaskID.捕获]: 0,
            [ConsumptionTaskID.盐罐]: 0,
            [ConsumptionTaskID.金砖]: 0,
        });
    };

    // 保存目标档位到配置
    const saveTargetStages = () => {
        if (!user || !updateConfig) {
            Notify.warn('无法保存配置，请检查用户信息', { duration: 1000 });
            return;
        }

        const currentTime = new Date().getTime();
        if (_this.lastUpdateConfigTime === 0 || currentTime - _this.lastUpdateConfigTime > 1000) {
            // 更新目标档位（忽略盐罐类型任务）
            user.config.activity.consumption.recruitTarget = selectedTasks[ConsumptionTaskID.招募] || 0;
            user.config.activity.consumption.boxTarget = selectedTasks[ConsumptionTaskID.宝箱] || 0;
            user.config.activity.consumption.fishingTarget = selectedTasks[ConsumptionTaskID.捕获] || 0;
            user.config.activity.consumption.goldTarget = selectedTasks[ConsumptionTaskID.金砖] || 0;

            setIsRequesting(true);
            updateConfig(user.config, () => {
                setIsRequesting(false);
                Notify.success('目标档位保存成功', { duration: 5000 });
            });
            _this.lastUpdateConfigTime = new Date().getTime();
        } else {
            Notify.warn(`请勿频繁操作, ${1 - Math.trunc((currentTime - _this.lastUpdateConfigTime) / 1000)}秒后可再次操作`, { duration: 1000 });
        }
    };

    // 防频繁操作引用
    const _this = useRef({
        lastUpdateConfigTime: 0
    }).current;

    // 初始化时根据用户配置设置默认选中的档位
    useEffect(() => {
        // 档位表未加载完成前不初始化，避免在空 groupedMissions 上固化 selections 为全0
        if (!act.missions?.length) {
            return;
        }
        if (!isInitialized && user && role) {
            const consumptionStatus = role?.actConsumptionStatus || {};
            const config = user.config?.activity?.consumption || {};

            const initialSelections = {};

            // 为每个任务类型找到能完成的最大档位
            const taskTypes = [
                { id: ConsumptionTaskID.招募, name: '招募', configKey: 'recruitTarget' },
                { id: ConsumptionTaskID.宝箱, name: '宝箱', configKey: 'boxTarget' },
                { id: ConsumptionTaskID.捕获, name: '捕获', configKey: 'fishingTarget' },
                { id: ConsumptionTaskID.金砖, name: '金砖', configKey: 'goldTarget' },
            ];

            taskTypes.forEach(taskType => {
                const targetFromConfig = config[taskType.configKey] || 0;
                const consumed = consumptionStatus[taskType.name] || 0;

                if (targetFromConfig > 0) {
                    // 获取该任务的所有档位配置
                    const taskConfigs = getTaskConfigs(groupedMissions, taskType.id);
                    let maxAchievableStage = 0;

                    // 找到能完成的最大档位（档位值 <= 配置目标 且 档位值 > 已消耗数量）
                    for (const stageConfig of taskConfigs) {
                        if (stageConfig.num > consumed && stageConfig.num <= targetFromConfig) {
                            maxAchievableStage = stageConfig.num;
                        }
                    }

                    initialSelections[taskType.id] = maxAchievableStage;
                } else {
                    initialSelections[taskType.id] = 0;
                }
            });

            // 盐罐任务不从配置读取，保持为0
            initialSelections[ConsumptionTaskID.盐罐] = 0;

            setSelectedTasks(initialSelections);
            setIsInitialized(true);
        }
    }, [isInitialized, user, role, groupedMissions, act.missions]);

    return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            {/* 概率统计 */}
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#e8f4fd', borderRadius: '5px' }}>
                <h3>开箱概率统计</h3>
                <p>仓库中{act.lv1ItemName}{level1Items}个/1000，已使用{act.lv1ItemName}{predictionInfo.totalUsedLv1}个</p>
                <p>仓库中{act.lv2ItemName}{level2Items}个/{targetLevel2}，已消耗{act.lv2ItemName}{consumedLv2Items}个</p>
                <p>实际开{act.lv2ItemName}概率: {(predictionInfo.item2ActualProbability * 100).toFixed(1)}% （理论概率25%，保底每5个必出1个）</p>
            </div>

            {/* 任务表格 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                        <th style={{ border: '1px solid #ddd', padding: '5px' }}>任务类型</th>
                        <th style={{ border: '1px solid #ddd', padding: '5px' }}>已消耗数量</th>
                        <th style={{ border: '1px solid #ddd', padding: '5px' }}>已完成档位</th>
                        <th style={{ border: '1px solid #ddd', padding: '5px' }}>预览目标档位</th>
                        <th style={{ border: '1px solid #ddd', padding: '5px' }}>{act.lv1ItemName}</th>
                    </tr>
                </thead>
                <tbody>
                    {taskData.map(task => (
                        <tr key={task.id}>
                            <td style={{ border: '1px solid #ddd', padding: '4px' }}>{task.name}</td>
                            <td style={{ border: '1px solid #ddd', padding: '4px' }}>{task.consumed}</td>
                            <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                {task.completedStages}/{task.maxStages}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                    {task.availableStages.map(stage => (
                                        <button
                                            key={stage.id}
                                            type="button"
                                            onClick={() => handleStageChange(task.id, stage.num)}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '3px',
                                                backgroundColor: task.selectedTargetNum === stage.num ? '#007bff' : '#f8f9fa',
                                                color: task.selectedTargetNum === stage.num ? 'white' : '#333',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                minWidth: '40px'
                                            }}
                                        >
                                            {stage.num}
                                        </button>
                                    ))}
                                    {task.availableStages.length === 0 && (
                                        <span style={{ color: '#999', fontSize: '12px' }}>无可用档位</span>
                                    )}
                                </div>
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                                {task.selectedTargetNum > 0 ? `+${task.predictedRewardLv1} 个` : '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* 预测结果 */}
            <div
                style={{
                    padding: '15px',
                    backgroundColor: predictionInfo.canReachTarget ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${predictionInfo.canReachTarget ? '#c3e6cb' : '#f5c6cb'}`,
                    borderRadius: '5px',
                    marginBottom: '20px',
                }}>
                <h3>预测结果</h3>
                {!predictionInfo.canReachTarget && (
                    <p>还需大约 {Math.ceil(predictionInfo.additionalLv1Needed)} 个{act.lv1ItemName}</p>
                )}
                <p>完成“目标档位”可再获得{act.lv1ItemName}: {predictionInfo.totalPredictedLv1} 个</p>
                <p>预计正常获得{act.lv2ItemName}: {predictionInfo.estimatedLv2} 个</p>
                <p>预计保底获得{act.lv2ItemName}: {predictionInfo.estimatedLv2AtLeast} 个</p>
                <p>预计总计正常获得{act.lv2ItemName}: {predictionInfo.estimatedLv2 + level2Items} 个，距离目标还差: {Math.max(0, predictionInfo.remainingLv2Needed - predictionInfo.estimatedLv2)} 个</p>
                <p>预计总计保底获得{act.lv2ItemName}: {predictionInfo.estimatedLv2AtLeast + level2Items} 个，距离目标还差: {Math.max(0, predictionInfo.remainingLv2Needed - predictionInfo.estimatedLv2AtLeast)} 个</p>
                <p style={{ fontWeight: 'bold', color: predictionInfo.canReachTarget ? '#155724' : '#721c24' }}>
                    按25%概率{predictionInfo.canReachTarget ? '✅ 可以达成目标' : '❌ 无法达成目标'}
                </p>
            </div>

            {/* 操作按钮 */}
            <div style={{ marginBottom: '30px' }}>
                <button
                    onClick={resetSelections}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        marginRight: '10px',
                    }}>
                    清除目标档位
                </button>
                <button
                    onClick={saveTargetStages}
                    disabled={isRequesting}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: isRequesting ? '#ccc' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: isRequesting ? 'not-allowed' : 'pointer',
                        marginRight: '10px',
                    }}>
                    {isRequesting ? '保存中...' : '把目标档位保存为设置'}
                </button>
            </div>
        </div>
    );
};

export default ConsumptionTaskView;
